/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { DefinitionTooltip, Link, TextArea, TextInput } from '@carbon/react';
import { Launch } from '@carbon/icons-react';
import { observer } from 'mobx-react';

import hasAccess, { actions } from 'utils/user-access';
import gitSettingsStore from 'features/git-sync/settings/GitSettingsStore';
import { isValidUrl } from 'features/git-sync/settings/fields/validations';
import { projectStore } from 'stores';

import * as Styled from './GitFields.styled';

export const FIELDS_VALIDATION = {
  clientId: { required: true },
  installationId: { required: true },
  pemKey: {
    required: true,
    customValidation: (value) => {
      // The PEM key value can be Bool(true) when received from the BE, or a string when typed by the user
      return (
        (typeof value === 'string' &&
          value.match(/-----BEGIN RSA PRIVATE KEY-----\s([A-Za-z0-9+/=\s]+)-----END RSA PRIVATE KEY-----/)) ||
        typeof value === 'boolean'
      );
    }
  },
  repositoryUrl: { required: true, customValidation: isValidUrl }
};

export const FIELDS_THAT_REQUIRE_RESYNC = ['repositoryUrl'];

const GitHubFields = ({ onChangeSetting, onInputBlur, errors }) => {
  const project = projectStore.project;
  const canModifySettings = hasAccess(project, actions.MODIFY_GIT_SYNC_SETTINGS);
  const canReplacePrivateKey =
    canModifySettings && gitSettingsStore.getCurrentSetting('pemKey') && gitSettingsStore.isSettingSaved('pemKey');
  const { fullRepositoryPath, isFullRepositoryPathValid } = gitSettingsStore;

  const handleChange = (event) => {
    const { id, value } = event.target;
    onChangeSetting(id, value);
  };

  const handleReplacePrivateKey = () => {
    onChangeSetting('pemKey', false);
  };

  const handleBlur = (event) => {
    const { id, value } = event.target;
    onInputBlur(id, value);
  };

  return (
    <>
      <Styled.FieldsGroupTitle>Provider details</Styled.FieldsGroupTitle>

      <TextInput
        id="clientId"
        labelText="Client ID"
        placeholder="Enter client ID"
        value={gitSettingsStore.getCurrentSetting('clientId')}
        invalid={Boolean(errors['clientId'])}
        invalidText={errors['clientId']}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <TextInput
        id="installationId"
        labelText="Installation ID"
        placeholder="Enter installation ID"
        value={gitSettingsStore.getCurrentSetting('installationId')}
        invalid={Boolean(errors['installationId'])}
        invalidText={errors['installationId']}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      {canReplacePrivateKey ? (
        <Styled.InlineButtons>
          <TextInput
            id="pemKey"
            labelText="Private key"
            placeholder="Enter PEM key"
            type="password"
            value="AcmeKey"
            disabled
          />
          <Styled.InlineDeleteButton size="sm" kind="tertiary" onClick={handleReplacePrivateKey}>
            Replace private key
          </Styled.InlineDeleteButton>
        </Styled.InlineButtons>
      ) : (
        <TextArea
          id="pemKey"
          rows={4}
          labelText="Private key"
          placeholder="-----BEGIN RSA PRIVATE KEY-----
...
-----END RSA PRIVATE KEY-----"
          invalid={Boolean(errors['pemKey'])}
          invalidText={errors['pemKey']}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      )}

      <Styled.FieldsGroupTitle>Repository details</Styled.FieldsGroupTitle>

      <TextInput
        id="repositoryUrl"
        labelText="GitHub repository URL"
        placeholder="https://github.com/"
        value={gitSettingsStore.getCurrentSetting('repositoryUrl')}
        invalid={Boolean(errors['repositoryUrl'])}
        invalidText={errors['repositoryUrl']}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <Styled.TextInput
        id="branchName"
        labelText={
          <DefinitionTooltip
            definition={
              <span role="tooltip">
                Specify the target branch to sync with your GitHub repository.
                <br />
                <br />
                Duplicate the process application in a different branch to allow multiple developers to work within
                feature branches on the same process.
                <br />
                <br />
                <Styled.TooltipLink
                  href="https://docs.camunda.io/docs/next/components/modeler/web-modeler/git-sync/"
                  target="_blank"
                >
                  Learn more about Git sync
                </Styled.TooltipLink>
              </span>
            }
            openOnHover
            align="right"
          >
            Branch
          </DefinitionTooltip>
        }
        placeholder="Enter branch name"
        value={gitSettingsStore.getCurrentSetting('branchName')}
        invalid={Boolean(errors['branchName'])}
        invalidText={errors['branchName']}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <Styled.TextInput
        id="path"
        labelText={
          <DefinitionTooltip
            definition={
              <span role="tooltip">
                Web Modeler only syncs files within the defined repository path.
                <br />
                <br />
                Change the path to organize multiple process applications within the same repository.
                <br />
                <br />
                <Styled.TooltipLink
                  href="https://docs.camunda.io/docs/next/components/modeler/web-modeler/git-sync/"
                  target="_blank"
                >
                  Learn more about Git sync
                </Styled.TooltipLink>
              </span>
            }
            openOnHover
            align="right"
          >
            Repository path (optional)
          </DefinitionTooltip>
        }
        placeholder="src/main/resources"
        value={gitSettingsStore.getCurrentSetting('path')}
        invalid={Boolean(errors['path'])}
        invalidText={errors['path']}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <Link disabled={!isFullRepositoryPathValid} href={fullRepositoryPath} target="_blank">
        Open repository&nbsp;
        <Launch />
      </Link>
    </>
  );
};

export default observer(GitHubFields);
